/**
 * Referral tracker — captures `?r=<code>` from the URL and persists it for
 * 30 days, so when the visitor joins the waitlist anywhere on the site we
 * include the referrer code in the join request.
 *
 * Drop-in import (no React state needed — just call once at app boot):
 *
 *     import { initReferralTracker, getReferrerCode } from "./useReferralTracker";
 *     initReferralTracker();
 *
 * Then in your waitlist form's submit handler:
 *
 *     await fetch("/api/waitlist/v2/join", {
 *       method: "POST",
 *       headers: { "Content-Type": "application/json" },
 *       body: JSON.stringify({ email, referrer_code: getReferrerCode() }),
 *     });
 */

const STORAGE_KEY = "rsvr_ref";
const STORAGE_KEY_PARTNER = "rsvr_partner";
const TTL_DAYS = 30;

const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,30}[a-z0-9]$/;

function isFreshEnough(ts) {
  if (!ts) return false;
  return Date.now() - ts < TTL_DAYS * 24 * 60 * 60 * 1000;
}

function writeKey(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify({ value, ts: Date.now() }));
  } catch {
    /* quota / disabled storage — silently skip */
  }
}

function readKey(key) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Old format used `code` instead of `value` — support both so existing
    // referrer codes don't get wiped by a deploy.
    const value = parsed?.value ?? parsed?.code ?? null;
    if (!value || !isFreshEnough(parsed?.ts)) return null;
    return value;
  } catch {
    return null;
  }
}

function clearKey(key) {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(key); } catch { /* ignore */ }
}

/**
 * Reads ?r=<code> from the query string AND /r/<slug> from the URL path,
 * persisting whichever is present. Safe to call repeatedly.
 *
 * Partner slug and personal-invite code are stored under separate keys
 * so they can coexist client-side. The server picks the winner on
 * signup (partner wins).
 */
export function initReferralTracker() {
  if (typeof window === "undefined") return;
  try {
    const params = new URLSearchParams(window.location.search);
    const code = (params.get("r") || params.get("ref") || "").trim().toLowerCase();
    if (code && /^[a-z0-9-]{3,64}$/.test(code)) {
      writeKey(STORAGE_KEY, code);
    }
    const path = window.location.pathname || "";
    const m = path.match(/^\/r\/([^\/?#]+)/i);
    if (m) {
      const slug = m[1].trim().toLowerCase();
      if (SLUG_RE.test(slug)) writeKey(STORAGE_KEY_PARTNER, slug);
    }
  } catch {
    /* SSR / quota / disabled storage — silently skip */
  }
}

/** Returns the stored referrer code or null. */
export function getReferrerCode() {
  return readKey(STORAGE_KEY);
}

/** Clears the stored code after a successful join. */
export function clearReferrerCode() {
  clearKey(STORAGE_KEY);
}

/** Returns the stored partner slug or null. */
export function getPartnerSlug() {
  return readKey(STORAGE_KEY_PARTNER);
}

/** Stores a partner slug explicitly (used by the /r/:slug route component). */
export function setPartnerSlug(slug) {
  const s = (slug || "").trim().toLowerCase();
  if (!s || !SLUG_RE.test(s)) return;
  writeKey(STORAGE_KEY_PARTNER, s);
}

/** Clears the stored partner slug after a successful signup. */
export function clearPartnerSlug() {
  clearKey(STORAGE_KEY_PARTNER);
}
